/**
 * Static fallback token list — used when the API is unreachable so the
 * token selector still shows supported tokens for browsing.  Addresses
 * are mainnet; the list is intentionally brief (one entry per unique
 * symbol-chain pair that the backend would seed).
 *
 * Lives in its own file (not api.ts) to avoid circular imports:
 *   api.ts → decimals.ts → api.ts
 */

export interface SupportedToken {
  symbol: string
  chainName: string
  address: string
  decimals: number
  type: string
}

export const FALLBACK_TOKENS: SupportedToken[] = [
  // Stablecoins — USDC on each bridgeable/dex-routable chain
  { symbol: 'USDC', type: 'stable', chainName: 'ethereum', address: '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48', decimals: 6 },
  { symbol: 'USDC', type: 'stable', chainName: 'base',     address: '0x833589fcd6edb6e08f4c7c32d4f71b54bda02913', decimals: 6 },
  { symbol: 'USDC', type: 'stable', chainName: 'arbitrum',  address: '0xaf88d065e77c8cc2239327c5edb3a432268e5831', decimals: 6 },
  { symbol: 'USDC', type: 'stable', chainName: 'optimism',  address: '0x0b2c639c533813f4aa9d7837caf62653d097ff85', decimals: 6 },
  { symbol: 'USDC', type: 'stable', chainName: 'polygon',   address: '0x3c499c542cef5e3811e1192ce70d8cc03d5c3359', decimals: 6 },
  { symbol: 'USDC', type: 'stable', chainName: 'solana',    address: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v', decimals: 6 },
  // Stablecoins — USDT
  { symbol: 'USDT', type: 'stable', chainName: 'ethereum', address: '0xdac17f958d2ee523a2206206994597c13d831ec7', decimals: 6 },
  { symbol: 'USDT', type: 'stable', chainName: 'base',     address: '0xfde4c96c8593536e31f229ea8f37b2ada2699bb2', decimals: 6 },
  { symbol: 'USDT', type: 'stable', chainName: 'arbitrum',  address: '0xfd086bc7cd5c481dcc9c85ebe478a1c0b69fcbb9', decimals: 6 },
  { symbol: 'USDT', type: 'stable', chainName: 'optimism',  address: '0x94b008aa00579c1307b0ef2c499ad98a8ce58e58', decimals: 6 },
  { symbol: 'USDT', type: 'stable', chainName: 'polygon',   address: '0xc2132d05d31c914a87c6611c10748aeb04b58e8f', decimals: 6 },
  { symbol: 'USDT', type: 'stable', chainName: 'solana',    address: 'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB', decimals: 6 },
  // Tron stablecoins
  { symbol: 'USDC', type: 'stable', chainName: 'tron',    address: 'TEkxiTehnzSmSe2XqrBj4w32RUN966rdz8', decimals: 6 },
  { symbol: 'USDT', type: 'stable', chainName: 'tron',    address: 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t', decimals: 6 },
  // #174 — USDG (Global Dollar, 6-dec). On-chain-verified 2026-07-23 (F2): all three are Global Dollar
  // USDG, NOT the 18-dec USDGLO look-alike. Issued on ethereum/solana/robinhood ONLY.
  { symbol: 'USDG', type: 'stable', chainName: 'ethereum',  address: '0xe343167631d89b6ffc58b88d6b7fb0228795491d', decimals: 6 },
  { symbol: 'USDG', type: 'stable', chainName: 'solana',    address: '2u1tszSeqZ3qBWF3uNGPFc8TzMk2tdiwknnRMWGWjGWH', decimals: 6 },
  { symbol: 'USDG', type: 'stable', chainName: 'robinhood', address: '0x5fc5360d0400a0fd4f2af552add042d716f1d168', decimals: 6 },
  // #174 — Robinhood native gas is ETH.
  { symbol: 'ETH',  type: 'native', chainName: 'robinhood', address: '0x0000000000000000000000000000000000000000', decimals: 18 },
  // BSC Binance-Peg USDC (18 decimals)
  { symbol: 'USDC', type: 'stable', chainName: 'bnb',     address: '0x8ac76a51cc950d9822d68b83fe1ad97b32cd580d', decimals: 18 },
  // BNB native token
  { symbol: 'BNB',  type: 'native', chainName: 'bnb',     address: '0x0000000000000000000000000000000000000000', decimals: 18 },
  // Native tokens
  { symbol: 'SOL', type: 'native', chainName: 'solana',   address: 'So11111111111111111111111111111111111111112', decimals: 9 },
  { symbol: 'ETH', type: 'native', chainName: 'ethereum', address: '0x0000000000000000000000000000000000000000', decimals: 18 },
  { symbol: 'ETH', type: 'native', chainName: 'base',     address: '0x0000000000000000000000000000000000000000', decimals: 18 },
  { symbol: 'ETH', type: 'native', chainName: 'arbitrum',  address: '0x0000000000000000000000000000000000000000', decimals: 18 },
  { symbol: 'ETH', type: 'native', chainName: 'optimism',  address: '0x0000000000000000000000000000000000000000', decimals: 18 },
  { symbol: 'POL', type: 'native', chainName: 'polygon',   address: '0x0000000000000000000000000000000000000000', decimals: 18 },
]
